import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import config from 'config'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import { AbsoluteFillStyle } from 'theme'
import styled from 'styled-components'

const propTypes = {
  videoUrl: PropTypes.string.isRequired,
  playbackVideoUrl: PropTypes.string.isRequired,
  onFinish: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  updateState: PropTypes.func,
  draftMode: PropTypes.bool,
  timedInterview: PropTypes.bool,
  pauseVideo: PropTypes.bool,
  subtitles: PropTypes.shape({
    url: PropTypes.string,
    language: PropTypes.string,
  }),
  englishSubtitles: PropTypes.shape({
    url: PropTypes.string,
    language: PropTypes.string,
  }),
  subtitleLabel: PropTypes.string,
  englishSubtitleLabel: PropTypes.string,
}

const defaultProps = {
  updateState: () => {},
  draftMode: false,
  pauseVideo: false,
  timedInterview: false,
  subtitles: undefined,
  englishSubtitles: undefined,
  subtitleLabel: 'Captions',
  englishSubtitleLabel: 'English',
}

const VideoPlayerContainer = styled.div`
  .video-js[tabindex='-1'] {
    ${AbsoluteFillStyle};
    display: block;
    cursor: pointer;
    z-index: 1;
  }

  .vjs-control-bar {
    display: flex;
  }

  .vjs-current-time,
  .vjs-time-divider,
  .vjs-duration {
    display: flex;
    align-items: center;
    font-size: ${({ theme }) => theme.space.s12};
  }

  .vjs-current-time,
  .vjs-duration {
    padding: 0;
  }

  .vjs-menu li {
    font-size: ${({ theme }) => theme.space.s12};
    font-family: ${({ theme }) => theme.font.primary};

    span {
      font-weight: 500;
    }
  }
`

const StyledPlayer = styled.div`
  display: block;
  cursor: pointer;
  ${AbsoluteFillStyle};
`

const VideoPlayer = ({
  videoUrl,
  subtitles,
  englishSubtitles,
  subtitleLabel,
  englishSubtitleLabel,
  playbackVideoUrl,
  onFinish,
  onError,
  updateState,
  draftMode,
  timedInterview,
  pauseVideo,
}) => {
  let videoNode = React.createRef(null)
  let player

  const videoUrlToHlsUrl = url => {
    return url.replace('?', '.flv.mp4/playlist.m3u8?')
  }

  const isHlsUrl = url => url && url.includes('.m3u8')

  const getCloudfrontSigningParams = url => {
    try {
      const parsed = new URL(url)
      const params = {}
      for (const key of ['Policy', 'Signature', 'Key-Pair-Id']) {
        if (parsed.searchParams.has(key)) {
          params[key] = parsed.searchParams.get(key)
        }
      }
      return Object.keys(params).length === 3 ? params : null
    } catch {
      return null
    }
  }

  const getSources = (playbackVideoUrl, videoUrl) => {
    if (isHlsUrl(playbackVideoUrl)) {
      return [
        {
          type: 'application/x-mpegurl',
          src: playbackVideoUrl,
        },
      ]
    }
    // You can set useHls in config to true if you need to view flash-recordings on localhost
    if (config.USE_HLS) {
      return [
        {
          type: 'application/x-mpegurl',
          src: `${config.VIDEO_URL_BASE}/${videoUrlToHlsUrl(videoUrl)}`,
        },
      ]
    }
    return [
      {
        type: 'video/mp4',
        src: playbackVideoUrl,
      },
    ]
  }

  const unloadPlayer = () => {
    if (player) {
      player.dispose()
      videoNode = null
    }
  }

  const loadPlayer = () => {
    const originalSubtitleTrack = subtitles &&
      subtitles.url && {
        src: subtitles.url,
        srclang: subtitles.language,
        kind: 'captions',
        label: subtitleLabel,
        default: true,
      }

    const englishSubtitleTrack = englishSubtitles &&
      englishSubtitles.url && {
        src: englishSubtitles.url,
        srclang: englishSubtitles.language || 'en',
        kind: 'captions',
        label: englishSubtitleLabel,
      }

    const subtitleTracks = [originalSubtitleTrack, englishSubtitleTrack].filter(Boolean)
    const hasSubtitles = subtitleTracks.length > 0

    const controlBarChildren = timedInterview
      ? {
          captionsButton: hasSubtitles,
        }
      : {
          playToggle: true,
          currentTimeDisplay: true,
          timeDivider: true,
          durationDisplay: true,
          progressControl: true,
          fullscreenToggle: true,
          playbackRateMenuButton: true,
          captionsButton: hasSubtitles,
        }
    const imageName =
      videoUrl.indexOf('archive.mp4') > -1
        ? videoUrl.split('/')[1]
        : videoUrl.replace(/\?.*$/, '')
    const videoJsOptions = {
      techOrder: ['html5'],
      html5: {
        nativeTextTracks: !hasSubtitles,
      },
      playbackRates: timedInterview ? null : [0.5, 1, 1.5, 2],
      bigPlayButton: false,
      textTrackSettings: false,
      controlBar: {
        children: controlBarChildren,
      },
      poster: `${config.USER_UPLOADS_BASE_URL}/b2c-screenshots/${imageName}.jpg`,
      controls: !draftMode,

      userActions: {
        doubleClick: false,
      },
      tracks: subtitleTracks,
      sources: getSources(playbackVideoUrl, videoUrl),
    }

    player = videojs(videoNode.current, videoJsOptions)

    if (isHlsUrl(playbackVideoUrl)) {
      const signingParams = getCloudfrontSigningParams(playbackVideoUrl)
      if (signingParams) {
        const masterUrl = new URL(playbackVideoUrl)
        const baseUrl = masterUrl.origin + masterUrl.pathname.substring(0, masterUrl.pathname.lastIndexOf('/') + 1)
        const appendSigningParams = options => {
          let uri = options.uri
          if (!uri.startsWith('http')) {
            uri = baseUrl + uri
          }
          const separator = uri.includes('?') ? '&' : '?'
          const qs = Object.entries(signingParams)
            .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
            .join('&')
          options.uri = `${uri}${separator}${qs}`
          return options
        }
        videojs.Vhs.xhr.beforeRequest = appendSigningParams
      }
    }

    player.on('error', () => {
      const error = player.error()
      if (error && error.code === 4) {
        // Code 4 is case when video is not found which usually means it is not converted yet
        // Because VideoJS is manipulating dom reloading is easier to handle on parent
        // (unmounting and remounting component by parent)
        onError()
      } else if (error) {
        onError()
      }
    })

    player.on('ready', () => {
      if (videoNode && videoNode.current) {
        const playPromise = videoNode.current.play()
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {})
        }
      }
    })

    player.on('play', event => {
      const videos = Array.prototype.slice.apply(
        document.getElementsByTagName('video')
      )

      // Pause other videos first before playing this one
      videos.forEach(video => {
        if (event.target.firstChild !== video) {
          video.pause()
        }
      })
        updateState('play')
    })

    player.on('pause', () => {
      if (draftMode) {
        onFinish()
      } else if (!timedInterview) {
        updateState('pause')
      }
    })

    if (timedInterview) {
      // if on timed interview pausing should not work so call play on pause
      player.on('pause', () => {
        if (player.paused()) {
          setTimeout(() => {
            const playPromise = player.play()
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {
                // Optionally log or ignore the AbortError here
              })
            }
          }, 50)
        }
      })
      // these are for disabling seeking (dragging time)
      // when video opens in the native video player on iOS
      let previousTime = 0

      player.on('timeupdate', () => {
        if (!player.seeking()) {
          previousTime = player.currentTime()
        }
      })

      player.on('seeking', () => {
        if (
          player.currentTime() > previousTime ||
          player.currentTime() < previousTime
        ) {
          player.currentTime(previousTime)
        }
      })
    }

    player.on('ended', () => {
      onFinish()
    })
  }

  useEffect(() => {
    loadPlayer()
    return () => {
      unloadPlayer()
    }
  }, [])

  useEffect(() => {
    if (pauseVideo && player) {
      player.pause()
    }
  }, [pauseVideo])

  return (
    <VideoPlayerContainer>
      <StyledPlayer data-vjs-player>
        <video ref={videoNode} className="video-js" playsInline />
      </StyledPlayer>
    </VideoPlayerContainer>
  )
}

VideoPlayer.propTypes = propTypes
VideoPlayer.defaultProps = defaultProps

export default VideoPlayer